import { Box, Container, Divider, Grid, Typography } from '@material-ui/core'
import { Controller } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router-dom'

import { Autocomplete } from '@/components/auto-complete'
import Button from '@/components/button'
import InputText from '@/components/input-text'
import { useGetPhysiologicalParams } from '@/hooks/queries/use-get-physiological-params'
import {
  DefaultPhysiologicalRegisterForm,
  usePhysiologicalRegisterForm,
} from '@/pages/demands/steps/physiological-register/hooks/use-physiological-register-form'
import { routes } from '@/routes/config'

import { useDiagnosisRegistrationStore } from '../../contexts/diagnosis-registration-store-context'
import { useStyles } from './styles'

export const PhysiologicalRegister = () => {
  const classes = useStyles()
  const navigate = useNavigate()

  const {
    registerData,
    physiologicalRegisterData,
    setPhysiologicalRegisterData,
  } = useDiagnosisRegistrationStore((state) => ({
    registerData: state.registerData,
    physiologicalRegisterData: state.physiologicalRegisterData,
    setPhysiologicalRegisterData: state.setPhysiologicalRegisterData,
  }))

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = usePhysiologicalRegisterForm({
    defaultValues: physiologicalRegisterData || undefined,
  })

  const { data, isError, isFetching, refetch, errorUpdateCount } =
    useGetPhysiologicalParams()

  async function onSubmit(data: DefaultPhysiologicalRegisterForm) {
    setPhysiologicalRegisterData(data)
    navigate(routes.Demands('select-files'))
  }

  if (!registerData) return <Navigate to={routes.Demands('register')} replace />

  return (
    <Container
      className={classes.main}
      component='form'
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box>
        <Box>
          <Typography variant={'h1'}>Novo Diagnóstico</Typography>
          <Typography variant={'h4'}>Dados do voo</Typography>
          <Grid container spacing={1} className={classes.container}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name='phenological_stage'
                defaultValue={null as any}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    id='phenological_stage'
                    options={data?.phenologicalStages || []}
                    onChange={(_, newValue: any) => onChange(newValue)}
                    freeSolo
                    value={value}
                    loading={isFetching}
                    optionsError={
                      !isFetching && isError && errorUpdateCount < 3
                    }
                    onRetry={() => refetch()}
                    noOptionsText='Não foi encontrado estádio fenológico.'
                    inputTextProps={{
                      label: 'Selecionar estádio fenológico',
                      error: (!isFetching && isError) || !!error,
                      helperText:
                        !isFetching && isError
                          ? 'Não foi encontrado estádio fenológico.'
                          : error?.message,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name='color_scale'
                defaultValue={null as any}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Autocomplete
                    id='color_scale'
                    options={data?.colorScales || []}
                    onChange={(_, newValue: any) => onChange(newValue)}
                    freeSolo
                    value={value}
                    loading={isFetching}
                    optionsError={
                      !isFetching && isError && errorUpdateCount < 3
                    }
                    onRetry={() => refetch()}
                    noOptionsText='Não foi encontrado escala de cor.'
                    inputTextProps={{
                      label: 'Selecionar escala de cor',
                      error: (!isFetching && isError) || !!error,
                      helperText:
                        !isFetching && isError
                          ? 'Não foi encontrado escala de cor.'
                          : error?.message,
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                id='min_temperature'
                type={'number'}
                label={'Intervalo mínimo de temperatura em C°'}
                {...register('min_temperature', { valueAsNumber: true })}
                error={!!errors.min_temperature}
                helperText={errors.min_temperature?.message}
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                id='max_temperature'
                type={'number'}
                label={'Intervalo máximo de temperatura em C°'}
                {...register('max_temperature', { valueAsNumber: true })}
                error={!!errors.max_temperature}
                helperText={errors.max_temperature?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                id='space_between_lines'
                type={'number'}
                label={'Espaço entre linhas em cm (OPCIONAL)'}
                {...register('space_between_lines')}
                error={!!errors.space_between_lines}
                helperText={errors.space_between_lines?.message}
                inputProps={{ step: 0.001 }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputText
                id='space_between_plants'
                type={'number'}
                label={'Espaço entre plantas em cm (OPCIONAL)'}
                {...register('space_between_plants')}
                error={!!errors.space_between_plants}
                helperText={errors.space_between_plants?.message}
                inputProps={{ step: 0.001 }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Divider />
      <Box className={classes.buttonContainer}>
        <Button outlined onClick={() => navigate(-1)}>
          Cancelar
        </Button>
        <Button type='submit'>Avançar</Button>
      </Box>
    </Container>
  )
}
